import { Forms, SystemText } from '@salesperformanceint/e4shared';
import React from 'react';
import { apiGetJson, apiPostJson } from 'services';

// Shared by both keys' fields.
const lookupText = path => SystemText(`Admin/Integrations/SFTP/Fields/KeyLookup/${path}`);

/**
 * Looks up the key served at `url`: { details } when we could ask ({ publicKey, … }, or null if none has been
 * generated), or { failed: true } when we couldn't (offline, the vault or the API failing). A failure is never
 * taken to mean there's no key, as generating one then would replace the key the company's server trusts.
 */
const lookUpKey = (url, user) =>
    apiGetJson(url, user.guid).then(
        response => ({ details: response?.publicKey ? response : null }),
        () => ({ failed: true })
    );

/** The public key served at `url`: { publicKey } (null if none has been generated), or { failed: true }. */
export const lookUpPublicKey = async (url, user) => {
    const result = await lookUpKey(url, user);
    return result.failed ? result : { publicKey: result.details?.publicKey || null };
};

// Ways the API can present a key (its `formats`), in the order offered.
const FORMATS = ['openSsh', 'ssh2'];

// Size the key box to the key: an Ed25519 key is a couple of lines, an RSA 4096 or PGP key many more.
// About 64 monospace characters fit across the modal; past 12 rows the box scrolls.
const CHARS_PER_ROW = 64;
export const keyRows = text =>
    Math.min(
        12,
        Math.max(
            2,
            (text || '').split('\n').reduce((rows, line) => rows + Math.max(1, Math.ceil(line.length / CHARS_PER_ROW)), 0)
        )
    );

/**
 * A key pair the company keeps on our side (in the secret store, not the settings), shown by its
 * public key: loads it from `url` (GET), generates or regenerates it there (POST), and lets the
 * admin copy it, or download it as `downloadName` when given.
 *
 * When the API sends them, it also offers the key's other `formats` (OpenSSH / SSH2) and shows its
 * `fingerprints` and `keyType`. With `keyTypes` (e.g. ['ed25519', 'rsa4096']) the admin picks the type
 * to generate, sent as { keyType } in the POST body.
 *
 * `onKeyChange(key, { generated })` reports the key once loaded (generated: false — it already
 * existed) and after each (re)generation (generated: true). Text comes from Admin/Integrations/SFTP/<textPath>/…
 */
export const PublicKeyField = ({
    url,
    textPath,
    user,
    error,
    downloadName,
    keyTypes,
    onKeyChange,
    'data-testid': testId,
}) => {
    const st = (path, ...extra) => SystemText(`Admin/Integrations/SFTP/${textPath}/${path}`, ...extra);

    // undefined = not loaded yet, null = none generated, else { publicKey, keyType?, formats?, fingerprints? }.
    const [details, setDetails] = React.useState(undefined);
    // The lookup failed, so whether there's a key is unknown: nothing is offered until it's tried again.
    const [lookupFailed, setLookupFailed] = React.useState(false);
    const [lookups, setLookups] = React.useState(0);
    const lookUpAgain = () => setLookups(n => n + 1);
    const [format, setFormat] = React.useState(FORMATS[0]);
    const [keyType, setKeyType] = React.useState(keyTypes?.[0]);
    const [generating, setGenerating] = React.useState(false);
    const [keyError, setKeyError] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    // Regenerating replaces the key, so it asks first.
    const [confirming, setConfirming] = React.useState(false);

    const showDetails = loaded => {
        setDetails(loaded);
        setCopied(false);
        // Regenerating defaults to the type the company already uses, when we offer it.
        if (keyTypes?.includes(loaded?.keyType)) setKeyType(loaded.keyType);
    };

    React.useEffect(() => {
        let cancelled = false;
        setDetails(undefined);
        setLookupFailed(false);
        lookUpKey(url, user).then(result => {
            if (cancelled) return;
            if (result.failed) {
                setLookupFailed(true);
                return;
            }
            showDetails(result.details);
            onKeyChange?.(result.details?.publicKey || null, { generated: false });
        });
        return () => {
            cancelled = true;
        };
    }, [url, lookups]);

    const generateKey = () => {
        setConfirming(false);
        setGenerating(true);
        setKeyError(null);
        // Only Regenerate (after its warning) replaces a key; the API refuses (409) to replace one otherwise.
        const replace = !!details?.publicKey;
        apiPostJson(url, keyTypes ? { keyType, replace } : { replace }, user.guid).then(
            response => {
                const generated = response?.publicKey ? response : null;
                showDetails(generated);
                setGenerating(false);
                onKeyChange?.(generated?.publicKey || null, { generated: true });
            },
            failure => {
                setGenerating(false);
                if (failure?.status === 409) {
                    // There's a key we didn't know about: show it, with Regenerate and its warning.
                    setKeyError(lookupText('Exists'));
                    lookUpAgain();
                } else {
                    setKeyError(st('Error/Generate'));
                }
            }
        );
    };

    const publicKey = details?.publicKey;
    const formats = details?.formats;
    const shownKey = formats?.[format] || publicKey;

    const copyKey = async () => {
        await navigator.clipboard.writeText(shownKey);
        setCopied(true);
    };

    const downloadKey = () => {
        const href = URL.createObjectURL(new Blob([shownKey], { type: 'text/plain' }));
        const link = document.createElement('a');
        link.href = href;
        link.download = downloadName;
        link.click();
        URL.revokeObjectURL(href);
    };

    const keyTypeName = type => (keyTypes?.includes(type) ? st(`KeyType/${type}`) : type);

    // Mandatory: there's always a type, so no Clear option.
    const keyTypePicker = keyTypes && (
        <Forms.Select
            key={`${testId}-keyType`}
            data-testid={`${testId}-keyType`}
            mandatory
            label={st('KeyType/Label')}
            help={st('KeyType/Help')}
            data={keyTypes.map(type => ({ id: type, name: keyTypeName(type) }))}
            value={keyType}
            onChange={setKeyType}
        />
    );

    // Picking a different type for an existing key only takes effect when it's regenerated.
    const typeChangePending = keyTypes && details?.keyType && keyType !== details.keyType;

    const shownError = keyError || error;

    return (
        <div data-testid={testId} className={`form-group ${shownError ? 'has-error' : ''}`}>
            <label>
                {st('Label')}
                <span className="mandatory">*</span>
                <span className="form-control-help">
                    <span className="copy">{st('Help')}</span>
                </span>
            </label>
            {lookupFailed ? (
                <div data-testid={`${testId}-lookupFailed`}>
                    <div className="mb10 text-danger">{lookupText('Failed')}</div>
                    <button className="btn btn-default" data-testid={`${testId}-lookupRetry`} onClick={lookUpAgain}>
                        {lookupText('Retry')}
                    </button>
                </div>
            ) : details === undefined ? (
                <div>
                    <i className="fa fa-spinner fa-spin" />
                </div>
            ) : publicKey ? (
                <>
                    {/* Choices first, then the key they shape, then what to do with it. */}
                    {formats && (
                        <Forms.Toggle
                            key={`${testId}-format`}
                            data-testid={`${testId}-format`}
                            label={st('Format/Label')}
                            help={st('Format/Help')}
                            yesLabel={st('Format/OpenSsh')}
                            noLabel={st('Format/Ssh2')}
                            value={format === FORMATS[0]}
                            onChange={useOpenSsh => {
                                setFormat(useOpenSsh ? FORMATS[0] : FORMATS[1]);
                                setCopied(false);
                            }}
                        />
                    )}
                    {keyTypePicker}
                    {typeChangePending && (
                        <div className="mtm10 mb10" data-testid={`${testId}-typeChange`}>
                            {st('KeyType/Change', keyTypeName(keyType))}
                        </div>
                    )}
                    <textarea
                        className="form-control mb10"
                        readOnly
                        rows={keyRows(shownKey)}
                        style={{ fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }}
                        value={shownKey}
                        onFocus={e => e.target.select()}
                    />
                    {((!keyTypes && details.keyType) || details.fingerprints) && (
                        <div className="mb10" data-testid={`${testId}-about`}>
                            {!keyTypes && details.keyType && <div>{st('KeyType/Current', keyTypeName(details.keyType))}</div>}
                            {details.fingerprints && (
                                <>
                                    <div>{st('Fingerprint')}</div>
                                    {/* SSH keys come with SHA256 and MD5 fingerprints, PGP keys with one of their own. */}
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px', overflowWrap: 'anywhere' }}>
                                        {Object.entries(details.fingerprints).map(([kind, fingerprint]) => (
                                            <div key={kind}>{fingerprint}</div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {/* Regenerate sits apart from Copy and Download, on the right. It's destructive, so it asks
                        first, with the warning and Yes/No on a line of their own below: the button stays put. */}
                    <div className="flex flex-center" style={{ flexWrap: 'wrap', rowGap: '10px' }}>
                        <button className="btn btn-default mr10" onClick={copyKey}>
                            <i className={`fa ${copied ? 'fa-check text-success' : 'fa-copy'} mr5`} />
                            {st(copied ? 'Copied' : 'Copy')}
                        </button>
                        {downloadName && (
                            <button className="btn btn-default mr10" onClick={downloadKey}>
                                <i className="fa fa-download mr5" />
                                {st('Download')}
                            </button>
                        )}
                        <button
                            className="btn btn-default"
                            style={{ marginLeft: 'auto' }}
                            data-testid={`${testId}-regenerate`}
                            disabled={generating || confirming}
                            onClick={() => setConfirming(true)}
                        >
                            {generating && <i className="fa fa-spinner fa-spin mr5" />}
                            {st('Regenerate')}
                        </button>
                    </div>
                    {confirming && (
                        <div className="mt10" data-testid={`${testId}-regenerateConfirm`}>
                            <div className="mb10">{st('Regenerate/Warning')}</div>
                            <button
                                className="btn btn-primary mr10"
                                data-testid={`${testId}-regenerateYes`}
                                onClick={generateKey}
                            >
                                <i className="fa fa-thumbs-up mr5" />
                                {SystemText('General/Controls/Warning/Buttons/Yes')}
                            </button>
                            <button
                                className="btn btn-default"
                                data-testid={`${testId}-regenerateNo`}
                                onClick={() => setConfirming(false)}
                            >
                                <i className="fa fa-thumbs-down mr5" />
                                {SystemText('General/Controls/Warning/Buttons/No')}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div>
                    <div className="mb10">{st('None')}</div>
                    {keyTypePicker}
                    <button className="btn btn-primary" disabled={generating} onClick={generateKey}>
                        {generating && <i className="fa fa-spinner fa-spin mr5" />}
                        {st('Generate')}
                    </button>
                </div>
            )}
            {shownError && <span className="help-block">{shownError}</span>}
        </div>
    );
};

export default PublicKeyField;
